import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../../utils/api";
import { getUsers, toggleUserStatus } from "../../services/adminService";
import toast from "react-hot-toast";

export default function UserList() {
  const { id } = useParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = id ? await api.get(`/users/${id}`) : await getUsers();
      const rawData = res.data?.data || res.data;

      const dataArray = Array.isArray(rawData) ? rawData : [rawData];
      const formattedData = dataArray.map((user) => ({
        ...user,
        // Ensure we use the status from the database
        is_active: user.is_active ?? true,
      }));
      setUsers(formattedData);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [id]); // fetchData only changes if 'id' changes

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Now 'fetchData' is a safe dependency

  // IMPLEMENTATION: Toggle status using the API
  const toggleStatus = async (userId) => {
    try {
      const res = await toggleUserStatus(userId);

      // Postman response shows: { "status": true, "message": "User Activated" }
      if (res.data.status) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, is_active: !user.is_active } : user,
          ),
        );
        toast.success(res.data.message || "Status Updated");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-8 bg-gray-50 min-h-screen font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">View User List</h2>
            <p className="text-sm text-gray-500">
              Manage your team members and their roles
            </p>
          </div>

          <div className="flex gap-2 w-full md:w-auto">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none w-full md:w-80 shadow-sm text-sm"
            />
            <button
              onClick={() => {
                setSearchTerm("");
                fetchData();
              }}
              className="bg-white border border-gray-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-100 transition shadow-sm"
            >
              Reset
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center p-20 text-gray-400 font-medium">
            Loading users...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl shadow-sm text-center border border-gray-200">
            <p className="text-gray-400 text-sm">
              No users found matching your search.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredUsers.map((user, index) => (
              <div
                key={user.id}
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition flex flex-col relative group"
              >
                {/* Top Section */}
                <div className="flex justify-between items-start mb-4">
                  <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                    {user.roles?.map((role, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[9px] font-bold uppercase rounded border border-indigo-100"
                      >
                        {typeof role === "string" ? role : role.name}
                      </span>
                    ))}
                  </div>
                </div>

                {/* User Info */}
                <div className="mb-6 flex-grow">
                  <span className="text-[9px] font-bold text-gray-400 uppercase block mb-0.5 tracking-tighter">
                    ID: {index + 1}
                  </span>
                  <h3 className="text-lg font-bold text-gray-800 leading-tight">
                    {user.name}
                  </h3>
                  <p className="text-black-400 mt-2 text-[12px] truncate">
                    {user.email}
                  </p>

                  {/* Reduced Font Size for Last Login */}
                  <p className="text-[9px] text-black-400 mt-3 font-medium uppercase tracking-tight">
                    Last Login:{" "}
                    <span className="font-normal normal-case italic">
                      {user.last_login || "Never"}
                    </span>
                  </p>
                </div>

                {/* Bottom Actions */}
                <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
                  {/* EDIT BUTTON LINK */}
                  {/* <a
                    href="/admin/users"
                    className="text-black border border-black px-3 py-1 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white transition-all rounded shadow-sm"
                  >
                    Edit User
                  </a> */}
                  {/* Switch Button */}
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-[9px] font-bold uppercase tracking-tighter ${user.is_active ? "text-emerald-600" : "text-gray-400"}`}
                    >
                      {user.is_active ? "Active" : "Inactive"}
                    </span>
                    <button
                      onClick={() => toggleStatus(user.id)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
                        user.is_active ? "bg-emerald-500" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                          user.is_active ? "translate-x-5" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
